import { Form, Formik, FormikActions } from 'formik';
import { rem } from 'polished';
import React, { MouseEvent, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '@unmind/design-system';
import { getChannel } from 'utils';
import useIrisToken from '../../App/Iris/useIrisToken';
import { tracking } from '../../App/Tracking';
import useFeatureFlag from '../../flags/useFeatureFlag';
import { LABEL_ERROR_MESSAGE } from '../../Shared/AlertView';
import { AlertBox } from '../../Shared/Form';
import { BodyText } from '../../Shared/Typography';
import { FormFields, ManageUsersAlert, postFile } from './ManageUsersShared';
import {
  AlertContainer,
  CancelButton,
  FileUploadButtonsContainer,
  FormField,
  FormWrapper,
  LoadingContainer,
  LoadingIndicatorText,
  ManageUsersBlurb,
  ManageUsersTitle,
  StyledLoadingIndicator,
  SubmitPanel,
  Wrapper,
} from './ManageUsers.styled';

const LeaversSubtext = styled(BodyText).attrs(({ theme }) => ({
  sizes: [theme.typography.fontSizes.fontSize12],
}))`
  margin-top: ${rem(4)};
`;

export const NO_USERS_REMOVED_ERROR =
  'An unexpected error has occurred. No users were removed.';

export type ManageLeaversProps = FormFields;

export const ManageLeavers = (props: ManageLeaversProps) => {
  const [currentFile, setCurrentFile] = useState<File | undefined>(undefined);
  const [currentStatus, setCurrentStatus] = useState<
    ManageUsersAlert | undefined
  >(undefined);
  const [usersToRemove, setUsersToRemove] = useState<number | undefined>(
    undefined,
  );
  const plusOneEnabled = useFeatureFlag('plus-one');

  const inputRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const { hasError, irisToken } = useIrisToken();
  const { getAccessTokenSilently, isAuthenticated: isAuthenticatedViaAuth0 } =
    useAuth0();
  const theme = useTheme();

  const clearFileInputField = () => {
    inputRef.current.value = '';
  };

  const onValidate = async (values: FormFields) => {
    const selectedFile = values.file as File;

    if (!currentFile) {
      setCurrentFile(selectedFile);
      const auth0JWT = isAuthenticatedViaAuth0
        ? await getAccessTokenSilently()
        : undefined;

      const resp = await postFile(
        `${process.env.REACT_APP_IRIS_BASE_URL}leavers/assess`,
        selectedFile,
        irisToken,
        auth0JWT,
      );
      if (resp) {
        if (resp.error) {
          setCurrentStatus({
            success: false,
            message: 'There was a problem with the file you selected.',
          });
          setCurrentFile(undefined);
          clearFileInputField();
        } else {
          setUsersToRemove(resp.usersToRemove);
        }
      }
    }
  };

  const onSubmit = async (
    values: FormFields,
    { setSubmitting, resetForm }: FormikActions<FormFields>,
  ) => {
    if (!currentFile) {
      setCurrentStatus({
        success: false,
        message: NO_USERS_REMOVED_ERROR,
      });
    } else {
      const auth0JWT = isAuthenticatedViaAuth0
        ? await getAccessTokenSilently()
        : undefined;

      const resp = await postFile(
        `${process.env.REACT_APP_IRIS_BASE_URL}leavers/remove`,
        currentFile,
        irisToken,
        auth0JWT,
      );

      if (resp) {
        if (resp.error || resp.usersRemoved === undefined) {
          setCurrentStatus({
            success: false,
            message: NO_USERS_REMOVED_ERROR,
          });
        } else {
          setCurrentStatus({
            success: true,
            message: `Successfully removed ${resp.usersRemoved.toLocaleString()} users.`,
          });
        }
        setCurrentFile(undefined);
        resetForm();
      }
    }
    setSubmitting(false);

    tracking.track('admin-leavers-submit-file');
  };

  // remove any existing status message and open the file chooser
  const chooseFile = (event: MouseEvent<HTMLButtonElement>) => {
    setCurrentStatus(undefined);
    inputRef.current.click();

    tracking.track('admin-leavers-choose-file');
    event.preventDefault();
  };

  const handleReset = (
    values: FormFields,
    { resetForm }: FormikActions<FormFields>,
  ) => {
    setCurrentStatus(undefined);
    setCurrentFile(undefined);
    setUsersToRemove(undefined);

    resetForm();
  };

  const isChooseFileDisabled = () => currentFile !== undefined;

  return (
    <Wrapper>
      <FormWrapper>
        <Formik
          initialValues={props}
          onSubmit={onSubmit}
          validate={onValidate}
          onReset={handleReset}
        >
          {({ isSubmitting, setFieldValue }) => (
            <Form>
              {(currentStatus || hasError) && (
                <AlertContainer>
                  {hasError ? (
                    <AlertBox
                      alertType="failed"
                      message={LABEL_ERROR_MESSAGE}
                    />
                  ) : currentStatus ? (
                    <AlertBox
                      alertType={currentStatus.success ? 'success' : 'failed'}
                      message={currentStatus.message}
                    />
                  ) : null}
                </AlertContainer>
              )}
              <FormField>
                <ManageUsersTitle id="leaversTitle">
                  Remove users
                </ManageUsersTitle>
                <ManageUsersBlurb>
                  When you remove a user they will immediately be informed that
                  they have 30 days left to sign in to {getChannel().brandName}.
                </ManageUsersBlurb>

                <input
                  ref={inputRef}
                  accept=".csv"
                  aria-labelledby="leaversTitle"
                  type="file"
                  style={{ display: 'none' }} // hidden to allow us to style the button and remove text
                  onChange={event => {
                    setFieldValue(
                      'file',
                      event.currentTarget.files
                        ? event.currentTarget.files[0]
                        : null,
                    );
                  }}
                />
                {isChooseFileDisabled() ? null : (
                  <Button
                    variant="primary"
                    label="Choose file"
                    onClick={chooseFile}
                    disabled={isChooseFileDisabled()}
                    ariaLabel="Upload Leavers File"
                  />
                )}
              </FormField>
              {currentStatus === undefined && isChooseFileDisabled() ? (
                <SubmitPanel>
                  <FormField>
                    {usersToRemove !== undefined ? (
                      <>
                        <BodyText
                          sizes={[theme.typography.fontSizes.fontSize16]}
                        >
                          {currentFile ? currentFile.name : ''}
                        </BodyText>
                        <BodyText
                          sizes={[
                            theme.typography.fontSizes.fontSize18,
                            theme.typography.fontSizes.fontSize20,
                            theme.typography.fontSizes.fontSize22,
                            theme.typography.fontSizes.fontSize24,
                          ]}
                        >
                          {usersToRemove.toLocaleString()}
                        </BodyText>
                        <BodyText
                          sizes={[theme.typography.fontSizes.fontSize16]}
                        >
                          user{usersToRemove !== 1 ? 's ' : ' '}will be removed
                        </BodyText>
                        {plusOneEnabled && (
                          <LeaversSubtext>
                            Please note that any Plus Ones will also be removed.
                          </LeaversSubtext>
                        )}
                      </>
                    ) : (
                      <LoadingContainer>
                        <StyledLoadingIndicator />
                        <LoadingIndicatorText>
                          Reviewing file...
                        </LoadingIndicatorText>
                      </LoadingContainer>
                    )}
                    <FileUploadButtonsContainer>
                      <Button
                        variant="primary"
                        id="submit"
                        name="submit"
                        disabled={usersToRemove === undefined}
                        label="Remove users"
                        loading={isSubmitting}
                        type="submit"
                      />
                      <CancelButton
                        label="Cancel"
                        disabled={usersToRemove === undefined || isSubmitting}
                        type="reset"
                      />
                    </FileUploadButtonsContainer>
                  </FormField>
                </SubmitPanel>
              ) : null}
            </Form>
          )}
        </Formik>
      </FormWrapper>
    </Wrapper>
  );
};

export default ManageLeavers;
